import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { createUser, updateUser, getUser } from "../../api/userApi";
import { normalizeEmail } from "../../utils/emailValidation";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from "../ui/table";

export const PermissionAction = {
  CREATE: "CREATE",
  READ: "READ",
  UPDATE: "UPDATE",
  DELETE: "DELETE",
  EXPORT: "EXPORT",
  NOTIFICATION: "NOTIFICATION",
};
export const ResourceType = {
  COURSE: "COURSE",
  TOPIC: "TOPIC",
  LEARNING_MATERIAL: "LEARNING_MATERIAL",
  TEST: "TEST",
  USER: "USER",
  ANALYTICS: "ANALYTICS",
  SUPPORT_TICKET: "SUPPORT_TICKET",
  ALL: "*",
};
const ACTIONS = Object.values(PermissionAction);
const RESOURCES = Object.values(ResourceType);

// Resources that should be displayed in the permissions table
const DISPLAY_RESOURCES = RESOURCES.filter(
  (resource) => !["TOPIC", "LEARNING_MATERIAL", "TEST"].includes(resource)
);

// Define the form schema
const userFormSchema = (isAdminAdd = false) =>
  z.object({
    name: z.string().min(2, { message: "Name must be at least 2 characters." }),
    email: z.string().email({ message: "Please enter a valid email address." }),
    ...(isAdminAdd
      ? {
          permissions: z
            .record(z.string(), z.array(z.string()))
            .refine((perms) => Object.keys(perms).length > 0, {
              message: "At least one permission must be selected.",
            }),
        }
      : {
          phone: z
            .string()
            .min(10, { message: "Please enter a valid phone number." }),
          role: z.enum(["admin", "seafarer"], {
            required_error: "Please select a role.",
          }),
        }),
    status: z.enum(["active", "inactive", "pending"], {
      required_error: "Please select a status.",
    }),
    password: z
      .string()
      .min(8, { message: "Password must be at least 8 characters." })
      .optional()
      .or(z.literal("")),
  });

export function UserForm({ userId, onSuccess, isAdminAdd = false }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing] = useState(!!userId);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(userFormSchema(isAdminAdd)),
    defaultValues: {
      name: "",
      email: "",
      ...(isAdminAdd ? { permissions: {} } : { phone: "", role: "seafarer" }),
      status: "active",
      password: "",
    },
  });

  // Fetch user data if in edit mode
  useEffect(() => {
    if (userId) {
      const fetchUser = async () => {
        try {
          setIsLoading(true);
          const user = await getUser(userId);
          Object.entries(user).forEach(([key, value]) => {
            setValue(key, value);
          });
        } catch (error) {
          console.error("Error fetching user:", error);
          toast.error("Failed to load user data");
        } finally {
          setIsLoading(false);
        }
      };

      fetchUser();
    }
  }, [userId, setValue]);

  // Helper function to automatically add related resources when COURSE is selected
  const handlePermissionChange = (action, resource, checked) => {
    const current = watch("permissions") || {};
    const updated = { ...current };

    if (!updated[action]) updated[action] = [];

    if (checked) {
      // Add the selected resource
      updated[action] = Array.from(new Set([...updated[action], resource]));

      // If COURSE is selected, automatically add TOPIC, LEARNING_MATERIAL, and TEST
      if (resource === "COURSE") {
        updated[action] = Array.from(
          new Set([...updated[action], "TOPIC", "LEARNING_MATERIAL", "TEST"])
        );
      }
    } else {
      // Remove the resource
      updated[action] = updated[action].filter((r) => r !== resource);

      // If COURSE is unselected, also remove TOPIC, LEARNING_MATERIAL, and TEST
      if (resource === "COURSE") {
        updated[action] = updated[action].filter(
          (r) => !["TOPIC", "LEARNING_MATERIAL", "TEST"].includes(r)
        );
      }
    }

    // Clean up invalid permissions based on backend validation
    const validPermissions = {
      CREATE: [
        "COURSE",
        "TOPIC",
        "LEARNING_MATERIAL",
        "TEST",
        "SUPPORT_TICKET",
        "*",
      ],
      READ: [
        "COURSE",
        "TOPIC",
        "LEARNING_MATERIAL",
        "TEST",
        "USER",
        "ANALYTICS",
        "SUPPORT_TICKET",
        "*",
      ],
      UPDATE: [
        "COURSE",
        "TOPIC",
        "LEARNING_MATERIAL",
        "TEST",
        "USER",
        "SUPPORT_TICKET",
        "*",
      ],
      DELETE: [
        "COURSE",
        "TOPIC",
        "LEARNING_MATERIAL",
        "TEST",
        "USER",
        "SUPPORT_TICKET",
        "*",
      ],
      EXPORT: ["ANALYTICS", "*"],
      NOTIFICATION: ["USER", "*"],
    };

    // Filter out invalid permissions
    if (validPermissions[action]) {
      updated[action] = updated[action].filter((r) =>
        validPermissions[action].includes(r)
      );
    }

    setValue("permissions", updated, { shouldValidate: true });
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);

      // Prepare user data based on form type (email lowercase for case-insensitivity)
      const userData = {
        name: data.name,
        email: normalizeEmail(data.email),
        status: data.status,
      };

      // Add password if provided
      if (data.password && data.password.trim() !== "") {
        userData.password = data.password;
      }

      if (isAdminAdd) {
        userData.role = "ADMIN";
        userData.permissions = data.permissions || {};
      } else {
        userData.role = data.role ? data.role.toUpperCase() : "SEAFARER";
        userData.mobileNumber = data.phone;
      }

      let result;
      if (isEditing) {
        result = await updateUser(userId, userData);
        toast.success("User updated successfully");
      } else {
        result = await createUser(userData);
        toast.success("User created successfully");
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate("/admin/users");
      }
    } catch (error) {
      console.error("Error saving user:", error);

      // Handle different types of errors
      let errorMessage = "Failed to save user";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate(-1)}
          className="h-8 w-8"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="sr-only">Back</span>
        </Button>
        <h2 className="text-2xl font-bold tracking-tight">
          {isEditing ? "Edit User" : "Add New Admin"}
        </h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
            <CardDescription>
              {isEditing
                ? "Update the user's details below."
                : "Enter the admin's details below. All fields are required."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  {...register("name")}
                  disabled={isLoading}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  {...register("email")}
                  disabled={isLoading || isEditing}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              {!isAdminAdd && (
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1234567890"
                    {...register("phone")}
                    disabled={isLoading}
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-500">
                      {errors.phone.message}
                    </p>
                  )}
                </div>
              )}

              {!isAdminAdd && (
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select
                    onValueChange={(value) => setValue("role", value)}
                    defaultValue={watch("role")}
                    disabled={isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="seafarer">Seafarer</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.role && (
                    <p className="text-sm text-red-500">
                      {errors.role.message}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  onValueChange={(value) => setValue("status", value)}
                  defaultValue={watch("status")}
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
                {errors.status && (
                  <p className="text-sm text-red-500">
                    {errors.status.message}
                  </p>
                )}
              </div>

              {!isEditing && (
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter password"
                    {...register("password")}
                    disabled={isLoading}
                  />
                  {errors.password && (
                    <p className="text-sm text-red-500">
                      {errors.password.message}
                    </p>
                  )}
                </div>
              )}
            </div>

            {isAdminAdd && (
              <div className="mt-6">
                <Label className="block mb-4 text-md">Assign Permissions</Label>
                <div className="mb-2 text-sm text-gray-600">
                  <strong>Note:</strong> When COURSE is selected, TOPIC,
                  LEARNING_MATERIAL, and TEST permissions are automatically
                  included.
                </div>
                <div className="overflow-x-auto">
                  <Table className="min-w-full border text-sm text-left">
                    <TableHeader>
                      <TableRow className="py-2">
                        <TableHead className="border px-2 py-1 bg-gray-50">
                          Action
                        </TableHead>
                        {DISPLAY_RESOURCES.map((resource) => (
                          <TableHead
                            key={resource}
                            className="border px-2 py-1 bg-gray-50"
                          >
                            {resource.replace(/_/g, " ")}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ACTIONS.map((action) => (
                        <TableRow key={action}>
                          <TableCell className="border px-2 py-1 font-medium">
                            {action}
                          </TableCell>
                          {DISPLAY_RESOURCES.map((resource) => {
                            const checked = (
                              watch("permissions")?.[action] || []
                            ).includes(resource);
                            return (
                              <TableCell
                                key={resource}
                                className="border px-2 py-1 text-left"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  className="accent-[var(--primary-font)]"
                                  disabled={
                                    (action === "NOTIFICATION" &&
                                      resource !== "USER") ||
                                    (action === "EXPORT" &&
                                      resource !== "ANALYTICS") ||
                                    (action === "CREATE" &&
                                      resource === "USER") ||
                                    (action === "CREATE" &&
                                      resource === "ANALYTICS") ||
                                    (action === "UPDATE" &&
                                      resource === "ANALYTICS") ||
                                    (action === "DELETE" &&
                                      resource === "ANALYTICS")
                                  }
                                  onChange={(e) => {
                                    handlePermissionChange(
                                      action,
                                      resource,
                                      e.target.checked
                                    );
                                  }}
                                  aria-label={`${action} ${resource}`}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {errors.permissions && (
                  <p className="text-sm text-red-500">
                    {errors.permissions.message}
                  </p>
                )}
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-end space-x-2 border-t px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? "Update User" : "Create Admin"}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
